// Single source of truth for the appraisal rating preview shown while filling the form.
// Mirrors the backend computation: KPI (80%) + Core Values (20%), where each section is the
// average of (item score / item max). Averaging keeps the fixed 80/20 split correct for ANY
// number of items, so a maxed-out sheet is always exactly 100% regardless of item count.

export const KPI_WEIGHT = 80
export const CORE_VALUES_WEIGHT = 20
export const KPI_MAX_SCORE = 10 // KPI dropdown: 1..10
export const CORE_VALUE_MAX_SCORE = 2 // Core value radio: Red=0, Yellow=1, Green=2

export type ScoreKey = 'employee_score' | 'manager_score'

type ScoredItem = {
  employee_score?: number | string
  manager_score?: number | string
}

type WeightedItem = ScoredItem & {
  relevance?: number | string
}

// Clamp a fraction into [0, 1]; treat non-finite/negative as 0 so bad data can't exceed a weight.
const clampFraction = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0
  return value > 1 ? 1 : value
}

// Average of (score / maxPerItem) across items, in [0, 1]. Empty list -> 0 (no divide-by-zero).
const averageFraction = (items: ScoredItem[], key: ScoreKey, maxPerItem: number): number => {
  if (!items?.length || maxPerItem <= 0) return 0
  const total = items.reduce(
    (sum, item) => sum + clampFraction(Number(item?.[key]) / maxPerItem),
    0,
  )
  return total / items.length
}

const weightedFraction = (
  items: WeightedItem[],
  key: ScoreKey,
  maxPerItem: number
): number => {
  if (!items?.length || maxPerItem <= 0) return 0

  return items.reduce((sum, item) => {
    const score = Number(item?.[key])
    const weight = Number(item?.relevance) || 0

    const normalized = clampFraction(score / maxPerItem)

    return sum + normalized * (weight / 100)
  }, 0)
}

// KPI contribution, 0..80.
export const kpiPart = (kpi: WeightedItem[] = [], key: ScoreKey): number =>
  weightedFraction(kpi, key, KPI_MAX_SCORE) * KPI_WEIGHT

// Core-values contribution, 0..20.
export const coreValuesPart = (coreValues: ScoredItem[] = [], key: ScoreKey): number =>
  averageFraction(coreValues, key, CORE_VALUE_MAX_SCORE) * CORE_VALUES_WEIGHT

// Total appraisal score, 0..100.
export const totalPercentage = (
  kpi: ScoredItem[] = [],
  coreValues: ScoredItem[] = [],
  key: ScoreKey,
): number => kpiPart(kpi, key) + coreValuesPart(coreValues, key)
